import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { useSlidingPill } from '@apps/web/hooks/use_sliding_pill';
import { cn } from '@apps/web/lib/utils';

// Mirrors the Radix root's active value so the list can position (and slide)
// the shared active-pill without reaching into Radix internals. Null when the
// sliding indicator is opted out or triggers render outside <Tabs>.
const TabsActiveValueContext = React.createContext<string | undefined | null>(
    null,
);

function Tabs({
    className,
    value,
    defaultValue,
    onValueChange,
    slidingIndicator = true,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & {
    // Set false when triggers restyle their active state (custom background
    // or border) — the sliding pill would paint behind that styling and the
    // slide adds nothing.
    slidingIndicator?: boolean;
}) {
    const [internalValue, setInternalValue] = React.useState(defaultValue);

    return (
        <TabsActiveValueContext.Provider
            value={slidingIndicator ? (value ?? internalValue) : null}
        >
            <TabsPrimitive.Root
                data-slot="tabs"
                className={cn('flex flex-col gap-2', className)}
                value={value}
                defaultValue={defaultValue}
                onValueChange={(next) => {
                    setInternalValue(next);
                    onValueChange?.(next);
                }}
                {...props}
            />
        </TabsActiveValueContext.Provider>
    );
}

function TabsList({
    className,
    children,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
    const activeValue = React.useContext(TabsActiveValueContext);
    const trackRef = React.useRef<HTMLDivElement>(null);
    const pill = useSlidingPill(
        trackRef,
        '[data-state="active"]',
        activeValue ?? undefined,
    );

    return (
        <TabsPrimitive.List
            ref={trackRef}
            data-slot="tabs-list"
            className={cn(
                // `isolate` scopes the pill's -z-10 so it paints behind the
                // trigger labels but not the page.
                'bg-muted text-muted-foreground relative isolate inline-flex h-9 w-fit items-center justify-center rounded-lg p-[3px]',
                className,
            )}
            {...props}
        >
            {activeValue !== null && pill !== null && (
                <span
                    aria-hidden="true"
                    className="bg-background dark:border-input dark:bg-input/30 absolute -z-10 rounded-md border border-transparent transition-[left,top,width,height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
                    style={pill}
                />
            )}
            {children}
        </TabsPrimitive.List>
    );
}

function TabsTrigger({
    className,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
    const activeValue = React.useContext(TabsActiveValueContext);

    return (
        <TabsPrimitive.Trigger
            data-slot="tabs-trigger"
            className={cn(
                "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:text-foreground text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                // Static active styling when the sliding pill is opted out or
                // the trigger renders outside <Tabs>.
                activeValue === null &&
                    'data-[state=active]:bg-background dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30',
                className,
            )}
            {...props}
        />
    );
}

function TabsContent({
    className,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
    return (
        <TabsPrimitive.Content
            data-slot="tabs-content"
            className={cn(
                'flex-1 outline-none data-[state=inactive]:hidden',
                className,
            )}
            {...props}
        />
    );
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
